/*!
 * Copyright (c) Microsoft Corporation. All rights reserved.
 */

import React, { useState, ReactElement } from "react";

import { useSelector } from "react-redux";

import { Field, Form, Formik } from "formik";
import * as Yup from "yup";

import { DefaultButton, PrimaryButton } from "@fluentui/react";
import { Stack } from "@fluentui/react/lib/Stack";

import * as TechnologyAreaSelectors from "Dataflows/TechnologyArea/TechnologyAreaSelector";

import FormikDropdown from "Components/Common/Dropdown/FormikDropdown";
import FormikMultiDropdown from "Components/Common/Dropdown/FormikMultiDropdown";
import { IGroupItem } from "Components/RegionalGroupedList/IGroupItem";
import RegionalGroupedList from "Components/RegionalGroupedList/RegionalGroupedList";
import { accessControlUserRole } from "Constants/AccessControlConstants";
import { RolesEnum } from "Constants/RolesPermissionsConstants";
import { AppState } from "Dataflows/Reducers";
import { IUserRole } from "Interfaces/AccessControl/IUserRole";
import { IUserRoleRegion } from "Interfaces/AccessControl/IUserRoleRegion";
import { getTenant } from "Utility/Utils";

import {
  containerStyles,
  horizontalGapStackTokens,
  verticalGapStackTokens,
  Error,
} from "./AccessControlRolesForm.styles";
import { IAccessControlRolesFormProps } from "./AccessControlRolesFormProps";

/**
 * The Access Control Role Validation Schema.
 */
const validationSchema = Yup.object().shape({
  userRoleName: Yup.string().trim().required(),
  technologyAreas: Yup.array().when("userRoleName", {
    is: (userRoleName: string) => userRoleName.includes(RolesEnum.MVP_TECH_AREA_VALIDATOR),
    then: Yup.array().min(1, "Please select at least 1 Technology Focus Area.").required(),
    otherwise: Yup.array().notRequired(),
  }),
  awardCategories: Yup.array().when("userRoleName", {
    is: (userRoleName: string) => userRoleName.includes(RolesEnum.MVP_EXECUTIVE),
    then: Yup.array().min(1, "Please select at least 1 Award Category.").required(),
    otherwise: Yup.array().notRequired(),
  }),
  userRegions: Yup.array().when("userRoleName", {
    is: (userRoleName: string) => userRoleName.includes("reviewer"),
    then: Yup.array().min(1, "Please select at least 1 Region, Country or State.").required(),
    otherwise: Yup.array().notRequired(),
  }),
});

const AccessControlRolesForm = (props: IAccessControlRolesFormProps): React.ReactElement => {
  const { tenant, actions, selectedUserId, selectedUserRole, technologyFocusAreas, geoData } =
    props;

  const awardCategories = useSelector((state: AppState) =>
    TechnologyAreaSelectors.getTechnologyAreaGroupOptionList(state.technologyAreaDataState)
  );

  let initValues: IUserRole = {
    id: 0,
    technologyAreas: [],
    userRegions: [],
    userRoleName: "",
    adminUserId: selectedUserId,
    awardCategories: [],
  };

  const isEditing = !!selectedUserRole;

  if (isEditing) {
    initValues = selectedUserRole;
    if (initValues.technologyAreas.length > 0) {
      initValues.technologyAreas = initValues.technologyAreas.map((x) => {
        return technologyFocusAreas.find((y) => y.key.includes(`${x}__`))?.key ?? x;
      });
    }
  }

  const role = accessControlUserRole[getTenant(tenant)].find(
    (x) => x.key === initValues.userRoleName
  );
  const [selectedRole, setSelectedRole] = useState<RolesEnum>(role?.key);
  const dismissPanel = (): void => {
    actions.setIsPanelOpen(false);
  };

  const roleFieldMap = [
    {
      roles: [RolesEnum.MVP_TECH_AREA_VALIDATOR],
      field: "technologyAreas",
    },
    {
      roles: [RolesEnum.MVP_REVIEWER, RolesEnum.MSA_REVIEWER, RolesEnum.RD_REVIEWER],
      field: "userRegions",
    },
    {
      roles: [RolesEnum.MVP_EXECUTIVE],
      field: "awardCategories",
    },
  ];

  const shouldRenderField = (selectedRole: RolesEnum | undefined, field: string): boolean => {
    return roleFieldMap.find((x) => x.roles.includes(selectedRole as RolesEnum))?.field === field;
  };

  return (
    <Formik
      initialValues={initValues}
      validationSchema={validationSchema}
      onSubmit={(values, { setSubmitting }): void => {
        actions.saveRole(values);
        setSubmitting(true);
        dismissPanel();
      }}
    >
      {({ values, setFieldValue, isValid, touched, errors }): React.ReactNode => (
        <Form>
          <Stack
            verticalAlign="space-between"
            styles={containerStyles}
            tokens={horizontalGapStackTokens}
          >
            <Stack tokens={verticalGapStackTokens}>
              <Stack.Item>
                <Field
                  component={FormikDropdown}
                  options={accessControlUserRole[tenant]}
                  disabled={isEditing}
                  onChange={(_, value): void => {
                    setFieldValue("technologyAreas", []);
                    setFieldValue("userRegions", []);
                    setFieldValue("userRoleName", value.key);
                    setSelectedRole(value.key);
                  }}
                  label="Role"
                  name="userRoleName"
                  placeholder="Select Role"
                />
              </Stack.Item>
              <Stack.Item>
                {shouldRenderField(selectedRole, "technologyAreas") && (
                  <>
                    <Field
                      component={FormikMultiDropdown}
                      options={technologyFocusAreas}
                      onChange={(_, value): void => {
                        let techAreas: string[] = [];
                        if (values.technologyAreas.includes(value.key)) {
                          techAreas = values.technologyAreas.filter((x) => x !== value.key);
                        } else {
                          techAreas = [...values.technologyAreas, value.key];
                        }
                        setFieldValue("technologyAreas", techAreas);
                      }}
                      label="Technology Areas"
                      name="technologyAreas"
                      placeholder="Select Technology Area"
                      required
                    />
                  </>
                )}

                {shouldRenderField(selectedRole, "userRegions") && (
                  <>
                    <RegionalGroupedList
                      geoData={geoData}
                      selectedData={initValues.userRegions}
                      onSelectionChanged={(selection: IGroupItem[]): void => {
                        const userRegions = selection.map((s) => {
                          return {
                            clRegionName: s.clRegionName,
                            countryOrRegionName: s.regionName,
                            stateOrProvinceName:
                              s.regionName === s.stateProvinceName ? "" : s.stateProvinceName,
                          } as IUserRoleRegion;
                        });
                        setFieldValue("userRegions", userRegions);
                      }}
                    />
                    {errors.userRegions && touched.userRegions ? (
                      <Error>{errors.userRegions}</Error>
                    ) : null}
                  </>
                )}

                {shouldRenderField(selectedRole, "awardCategories") && (
                  <>
                    <Field
                      component={FormikMultiDropdown}
                      options={awardCategories}
                      onChange={(_, value): void => {
                        let awardCategories: string[] = [];
                        if (values.awardCategories.includes(value.key)) {
                          awardCategories = values.awardCategories.filter((x) => x !== value.key);
                        } else {
                          awardCategories = [...values.awardCategories, value.key];
                        }
                        setFieldValue("awardCategories", awardCategories);
                      }}
                      label="Award Categories"
                      name="awardCategories"
                      placeholder="Select Award Category"
                      required
                    />
                  </>
                )}
              </Stack.Item>
            </Stack>
            <Stack.Item>
              <Stack horizontal tokens={horizontalGapStackTokens}>
                <PrimaryButton text="Save" type="submit" />
                <DefaultButton text="Cancel" onClick={dismissPanel} />
              </Stack>
            </Stack.Item>
          </Stack>
        </Form>
      )}
    </Formik>
  );
};

export default AccessControlRolesForm;
